import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthProvider";
import { LoadingSpinner } from "@/components/LoadingSpinner";

interface RequireOnboardingProps {
  children: React.ReactNode;
}

export function RequireOnboarding({ children }: RequireOnboardingProps) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) {
        setIsChecking(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error checking onboarding status:", error);
          // Default to completed to prevent blocking users
          setOnboardingCompleted(true);
        } else {
          setOnboardingCompleted(data?.onboarding_completed ?? false);
        }
      } catch (err) {
        console.error("Error checking onboarding:", err);
        setOnboardingCompleted(true);
      } finally {
        setIsChecking(false);
      }
    };

    if (ready) {
      checkOnboarding();
    }
  }, [user, ready]);

  if (!ready || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // If not logged in, let other guards handle it
  if (!user) {
    return <>{children}</>;
  }

  // If onboarding not completed, redirect to onboarding
  if (onboardingCompleted === false) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
